"use client";
import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import DOMPurify from "dompurify";
import md from "@/app/utils/markdown";
import styles from "./index.module.scss";

interface TypewriterFallbackProps {
  content: string;
  streaming?: boolean;
  error?: boolean;
  /** 历史/静态：立即展示全文，无打字机与光标 */
  instant?: boolean;
  onContentUpdate?: () => void;
  onTypingEnd?: () => void;
}

const CHARS_PER_FRAME = 8;
const FRAME_INTERVAL = 30;

/**
 * Streamdown 不可用时的回退组件。
 * 使用 markdown-it 渲染 + 逐字符打字机动画模拟流式效果。
 */
export default function TypewriterFallback({
  content,
  streaming = true,
  error = false,
  instant = false,
  onContentUpdate,
  onTypingEnd,
}: TypewriterFallbackProps) {
  const [displayedLen, setDisplayedLen] = useState(0);
  const animFrameRef = useRef<number | null>(null);
  const onContentUpdateRef = useRef(onContentUpdate);
  const onTypingEndRef = useRef(onTypingEnd);
  const hasCalledTypingEndRef = useRef(false);
  const prevContentLenRef = useRef(0);

  useEffect(() => {
    onContentUpdateRef.current = onContentUpdate;
    onTypingEndRef.current = onTypingEnd;
  });

  useEffect(() => {
    if (!content) {
      hasCalledTypingEndRef.current = false;
      setDisplayedLen(0);
      prevContentLenRef.current = 0;
    }
  }, [content]);

  useLayoutEffect(() => {
    if (instant && content) {
      setDisplayedLen(content.length);
    }
  }, [instant, content]);

  const notifyTypingEnd = () => {
    if (!hasCalledTypingEndRef.current && content) {
      hasCalledTypingEndRef.current = true;
      onTypingEndRef.current?.();
    }
  };

  useEffect(() => {
    if (instant && content) {
      notifyTypingEnd();
      return;
    }

    if (error && content) {
      setDisplayedLen(content.length);
      notifyTypingEnd();
      return;
    }

    if (!content) return;

    if (!streaming && displayedLen >= content.length) {
      notifyTypingEnd();
      return;
    }

    let lastTs = 0;
    const step = (ts: number) => {
      if (ts - lastTs >= FRAME_INTERVAL) {
        lastTs = ts;
        setDisplayedLen((prev) => {
          const next = Math.min(prev + CHARS_PER_FRAME, content.length);
          if (next >= content.length && !streaming) {
            notifyTypingEnd();
          }
          return next;
        });
      }
      animFrameRef.current = requestAnimationFrame(step);
    };
    animFrameRef.current = requestAnimationFrame(step);

    return () => {
      if (animFrameRef.current !== null) {
        cancelAnimationFrame(animFrameRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, streaming, error, instant]);

  useEffect(() => {
    if (content && displayedLen > prevContentLenRef.current) {
      prevContentLenRef.current = displayedLen;
      onContentUpdateRef.current?.();
    }
  }, [displayedLen, content]);

  const renderedHtml = useMemo(() => {
    if (!content) return "";
    const visibleText =
      error || instant ? content : content.slice(0, displayedLen);

    if (!visibleText) return "";

    try {
      return DOMPurify.sanitize(md.render(visibleText), {
        ADD_ATTR: ["target"],
      });
    } catch {
      return DOMPurify.sanitize(visibleText);
    }
  }, [content, displayedLen, error, instant]);

  const showCursor =
    !error &&
    !instant &&
    (streaming || displayedLen < content.length) &&
    content;

  return (
    <div className={styles.aiAnswerContent}>
      <span dangerouslySetInnerHTML={{ __html: renderedHtml }} />
      {showCursor && <span className={styles.cursor}>|</span>}
    </div>
  );
}
